import axios from 'axios';
import type { AdministrativeBoundarySummary, AdministrativeBoundary } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

// Log API_URL once on module initialization for diagnostics
console.log('[administrative-boundaries-api] API_URL:', API_URL);

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

/**
 * Cliente API para límites administrativos
 *
 * @remarks
 * Proporciona métodos para consultar límites administrativos desde el backend.
 * Los límites se cargan desde archivos GeoJSON estáticos.
 *
 * Casos de uso:
 * - Dropdown de selección de área en formulario de proyectos
 * - Búsqueda tipo-ahead de departamentos/municipios
 * - Obtención de geometrías completas para operaciones espaciales
 *
 * @example
 * ```typescript
 * import { administrativeBoundariesApi } from '@/core/spatial';
 *
 * // Buscar departamentos
 * const results = await administrativeBoundariesApi.getArgentinaDepartamentos('Bahía');
 * // => [{ id: '06_490', name: 'Bahía Blanca', ... }]
 *
 * // Obtener departamento específico con geometría
 * const boundary = await administrativeBoundariesApi.getArgentinaDepartamentoById('06_490');
 * // => { ..., geometry: { type: 'Polygon', coordinates: [...] } }
 * ```
 *
 * @version 1.0
 * @date 2026-03-16
 */
export const administrativeBoundariesApi = {
  /**
   * Obtiene lista de departamentos de Argentina
   *
   * @param searchTerm - Término de búsqueda opcional (filtra por nombre)
   * @param includeGeometry - Si true, incluye geometría completa (default false)
   * @returns Array de departamentos
   *
   * @example
   * ```typescript
   * // Búsqueda rápida (sin geometría para performance)
   * const results = await administrativeBoundariesApi.getArgentinaDepartamentos('San Juan');
   * // => [{ id: '70_007', name: 'Capital', fullName: 'San Juan - Capital', ... }]
   *
   * // Obtener todos para dropdown
   * const all = await administrativeBoundariesApi.getArgentinaDepartamentos();
   * // => Array de 529 departamentos
   * ```
   */
  async getArgentinaDepartamentos(
    searchTerm?: string,
    includeGeometry: boolean = false
  ): Promise<AdministrativeBoundarySummary[]> {
    const token = getAuthToken();

    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (includeGeometry) params.append('includeGeometry', 'true');

    const queryString = params.toString();
    const url = `${API_URL}/administrative-boundaries/argentina/departamentos${queryString ? '?' + queryString : ''}`;

    const response = await axios.get<AdministrativeBoundarySummary[]>(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    return response.data;
  },

  /**
   * Obtiene un departamento específico por ID (con geometría completa)
   *
   * @param id - ID del departamento (formato: "codigo_gid", ej: "06_490")
   * @returns Departamento con geometría completa
   * @throws Error si el departamento no existe
   *
   * @example
   * ```typescript
   * const boundary = await administrativeBoundariesApi.getArgentinaDepartamentoById('06_490');
   * // => {
   * //   id: '06_490',
   * //   name: 'Bahía Blanca',
   * //   fullName: 'Buenos Aires - Bahía Blanca',
   * //   geometry: { type: 'Polygon', coordinates: [...] }
   * // }
   * ```
   */
  async getArgentinaDepartamentoById(id: string): Promise<AdministrativeBoundary> {
    const token = getAuthToken();

    const response = await axios.get<AdministrativeBoundary>(
      `${API_URL}/administrative-boundaries/argentina/departamentos/${id}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );

    return response.data;
  },

  /**
   * Obtiene estadísticas sobre límites administrativos cargados
   *
   * @returns Metadata sobre límites disponibles
   *
   * @example
   * ```typescript
   * const stats = await administrativeBoundariesApi.getStats();
   * // => {
   * //   argentina: {
   * //     departamentos: { count: 529, loaded: true }
   * //   }
   * // }
   * ```
   */
  async getStats(): Promise<any> {
    const token = getAuthToken();

    const response = await axios.get(
      `${API_URL}/administrative-boundaries/stats`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );

    return response.data;
  },
};
